import {
  Button,
  Card,
  CardContent,
  PageHeader,
  SelectField,
  StatusChip,
  TextareaField,
} from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useGift, useGiftAction } from "../../features/ops/queries.ts";
import { GIFT_STAFF_REJECT_REASONS, type GiftStaffRejectReason } from "../../features/ops/types.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/gifts/$giftId")({ component: GiftDetailScreen });

// FR-STAFF-061/063: a small fixed taxonomy + free-text "other", bilingual.
const reasonLabels: Record<GiftStaffRejectReason, string> = {
  out_of_stock: "Out of stock / ของหมด",
  recipient_left: "Recipient left / ผู้รับออกจากโต๊ะ",
  safety_concern: "Safety concern / กังวลด้านความปลอดภัย",
  other: "Other (specify) / อื่นๆ (ระบุ)",
};

function GiftDetailScreen() {
  const principal = getStaffPrincipal();
  const canFulfil = hasPermission(principal, "gift:fulfil");
  const canReject = hasPermission(principal, "gift:reject");
  const { giftId } = Route.useParams();
  const { data: gift } = useGift(giftId);
  const action = useGiftAction();
  const [reasonCode, setReasonCode] = useState<GiftStaffRejectReason>("out_of_stock");
  const [otherText, setOtherText] = useState("");
  if (!gift) return <PageHeader title="Gift detail" subtitle="Loading gift…" />;

  const reason = reasonCode === "other" ? otherText.trim() : reasonLabels[reasonCode];
  const canSubmitReject = canReject && reason.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Gift ${gift.id}`}
        subtitle={`${gift.senderTableLabel} to ${gift.recipientTableLabel}`}
      />
      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="flex gap-2">
            <StatusChip tone={gift.status === "accepted" ? "info" : "neutral"}>
              {gift.status}
            </StatusChip>
            {gift.alcohol ? <StatusChip tone="warning">alcohol flag</StatusChip> : null}
          </div>
          {gift.items.map((line) => (
            <div key={line.id} className="flex justify-between border-border border-b py-2">
              <span>
                {line.qty}× {line.name} / {line.nameTh}
              </span>
              <span>{formatMoney(line.qty * line.priceMinor)}</span>
            </div>
          ))}
          <p className="text-muted-foreground text-sm">
            Payment state: {gift.refundState ?? "authorized"}
          </p>
          <p className="rounded-md bg-muted p-3 text-sm">
            Recipient identity is not disclosed. Reporter/sender contact handles are not shown.
          </p>
        </CardContent>
      </Card>
      {gift.status === "accepted" ? (
        <Card>
          <CardContent className="space-y-4 p-5">
            <SelectField
              id="giftReasonCode"
              label="Staff reject reason / เหตุผลปฏิเสธ"
              value={reasonCode}
              onChange={(value) => setReasonCode(value as GiftStaffRejectReason)}
              options={GIFT_STAFF_REJECT_REASONS.map((code) => ({
                value: code,
                label: reasonLabels[code],
              }))}
            />
            {reasonCode === "other" ? (
              <TextareaField
                id="giftReasonOther"
                label="Describe the reason / ระบุเหตุผล"
                value={otherText}
                onChange={setOtherText}
                placeholder="Required"
              />
            ) : null}
            <div className="flex gap-2">
              <Button
                disabled={!canFulfil}
                title={canFulfil ? undefined : "Requires gift:fulfil"}
                onClick={() => action.mutate({ giftId: gift.id, action: "fulfil" })}
              >
                Fulfil gift
              </Button>
              <Button
                variant="destructive"
                disabled={!canSubmitReject}
                title={
                  !canReject
                    ? "Requires gift:reject"
                    : reason.length === 0
                      ? "A reason is required"
                      : undefined
                }
                onClick={() => action.mutate({ giftId: gift.id, action: "reject", reason })}
              >
                Staff reject + refund/void
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
